import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getUserWorkspaces, type WorkspaceId } from '../config/workspaces';
import type { IUser } from '../types/auth.types';
import { useAppDispatch } from './useAppDispatch';
import { setUser } from '../store/features/auth/authSlice';
import {
  isWorkspaceAccessible,
  resolveDisplayedWorkspace,
  resolvePathLockedWorkspace,
} from '../utils/resolveDefaultWorkspace';
import {
  persistLastSelectedWorkspace,
  resolveLastSelectedWorkspace,
} from '../utils/workspacePreferences';
import { useCreatorStudioEntry } from './useCreatorStudioEntry';

/**
 * Shared workspace tab state for account dropdown + mobile sheet.
 * URL-specific routes lock the tab; neutral routes (404, shop, home) keep manual selection.
 */
export function useWorkspaceSelection(user: IUser) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { onboardingComplete, onboardingLoading } = useCreatorStudioEntry();
  const lastSelectedWorkspace = useMemo(
    () => resolveLastSelectedWorkspace(user),
    [user],
  );

  const pathLockedWorkspace = useMemo(
    () =>
      resolvePathLockedWorkspace(user, location.pathname, {
        creatorOnboardingComplete: onboardingComplete,
      }),
    [user, location.pathname, onboardingComplete],
  );

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<WorkspaceId>(() =>
    resolveDisplayedWorkspace({
      user,
      pathname: location.pathname,
      lastSelectedWorkspace,
      creatorOnboardingComplete: null,
    }),
  );

  useEffect(() => {
    if (pathLockedWorkspace) {
      setSelectedWorkspaceId(pathLockedWorkspace);
    }
  }, [pathLockedWorkspace]);

  useEffect(() => {
    if (pathLockedWorkspace) return;
    if (
      lastSelectedWorkspace &&
      isWorkspaceAccessible(user, lastSelectedWorkspace, {
        creatorOnboardingComplete: onboardingComplete,
      })
    ) {
      setSelectedWorkspaceId(lastSelectedWorkspace);
    }
  }, [pathLockedWorkspace, lastSelectedWorkspace, user, onboardingComplete]);

  const availableWorkspaceIds = useMemo(
    () =>
      new Set(
        getUserWorkspaces(user, { creatorOnboardingComplete: onboardingComplete }).map(
          (ws) => ws.id,
        ),
      ),
    [user, onboardingComplete],
  );

  const handleWorkspaceSelect = useCallback(
    (workspaceId: WorkspaceId) => {
      setSelectedWorkspaceId(workspaceId);
      void persistLastSelectedWorkspace(user, workspaceId, {
        creatorOnboardingComplete: onboardingComplete,
      }).then((updatedUser) => {
        if (updatedUser) dispatch(setUser(updatedUser));
      });
    },
    [dispatch, onboardingComplete, user],
  );

  return {
    selectedWorkspaceId,
    handleWorkspaceSelect,
    availableWorkspaceIds,
    hasMultipleWorkspaces: availableWorkspaceIds.size > 1,
    onboardingComplete,
    onboardingLoading,
  };
}
